import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A toast: one short line that says something went through — «Saved», «Rule paused» — and leaves on
 * its own. `Notice` stays where it was put, beside what it is about, until it is dismissed; a toast is
 * for what needs no place on the page and no answer. A refusal a person must act on is neither: it is
 * a dialog.
 *
 * There is one at a time, for the whole application: a second replaces the first, so a burst of
 * saves never stacks a tower of lines over the page. A unit calls `sdk.ui.toast(text, { tone })` (or
 * the `useToast()` hook, the same function); the shell draws `ToastHost` once, at the root, and that
 * host is the only place one appears. The store lives here, outside React, so whichever bundle calls
 * `toast` the shell's host is the one that shows it — the unit is handed the shell's `sdk.ui`.
 *
 * It is announced politely (`role="status"`, a region mounted empty from the start, because a live
 * region created together with its words is not read out), stays while the pointer is on it or the
 * keyboard is in it, and starts its time again when they leave.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { IconButton, TONE_FILL, cx } from "./ui.js";
export const TOAST_DURATION = 5000;
const SHORTEST = 2000;
let current = null;
let counter = 0;
let timer = null;
const listeners = new Set();
function publish(next) {
    current = next;
    for (const l of listeners)
        l();
}
function stopClock() {
    if (timer !== null)
        clearTimeout(timer);
    timer = null;
}
function startClock(state) {
    stopClock();
    timer = setTimeout(() => dismissToast(state.id), state.duration);
}
/** Show a toast, replacing the one on screen. Answers its id, which `dismissToast` takes. */
export function toast(text, options = {}) {
    const state = {
        id: ++counter,
        text,
        tone: options.tone ?? 'neutral',
        duration: Math.max(SHORTEST, options.duration ?? TOAST_DURATION),
        held: false,
    };
    publish(state);
    startClock(state);
    return state.id;
}
/** Take the toast away — only if it is still the one with this id, so a stale timer removes nothing newer. Absent: whichever is shown. */
export function dismissToast(id) {
    if (!current || (id !== undefined && current.id !== id))
        return;
    stopClock();
    publish(null);
}
/** The pointer is on it or the keyboard is in it: it stays. */
export function holdToast(id) {
    if (!current || current.id !== id || current.held)
        return;
    stopClock();
    publish({ ...current, held: true });
}
/** They left: its time starts again, whole, so nobody loses a line they had just started reading. */
export function releaseToast(id) {
    if (!current || current.id !== id || !current.held)
        return;
    const next = { ...current, held: false };
    publish(next);
    startClock(next);
}
/** The toast on screen, or null. For the host and for a test. */
export function currentToast() {
    return current;
}
export function subscribeToast(listener) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
/** The same `toast`, as a hook — for a component that would rather not reach for `sdk.ui.toast`. */
export function useToast() {
    return toast;
}
/**
 * Where the toast appears: the foot of the window, centred, above the page and above an open
 * dialog — it is a popover in the browser's top layer, as the menus are, so a modal's backdrop does
 * not cover it. Drawn once, by the shell. The status region is always there, empty when nothing is
 * shown, so a reader hears each toast as it arrives.
 */
export function ToastHost({ dismissLabel, className }) {
    const shown = useSyncExternalStore(subscribeToast, currentToast, currentToast);
    const layer = useRef(null);
    useEffect(() => {
        const el = layer.current;
        if (!el || typeof el.showPopover !== 'function')
            return;
        // Shown again on every toast, so it is raised above a dialog opened since the last one.
        try {
            if (el.matches(':popover-open'))
                el.hidePopover();
            el.showPopover();
        }
        catch { /* not in the document yet */ }
    }, [shown?.id]);
    return (_jsx("div", { ref: layer, popover: "manual", "data-toast-host": "", className: cx('pointer-events-none fixed inset-x-4 bottom-6 top-auto m-0 h-auto w-auto overflow-visible border-0 bg-transparent p-0', className), children: _jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "flex w-full justify-center", children: shown ? (_jsxs("div", { "data-toast": shown.tone, "data-held": shown.held ? '' : undefined, onMouseEnter: () => holdToast(shown.id), onMouseLeave: () => releaseToast(shown.id), onFocus: () => holdToast(shown.id), onBlur: (e) => { if (!e.currentTarget.contains(e.relatedTarget))
                    releaseToast(shown.id); }, className: "pointer-events-auto flex w-fit max-w-[min(32rem,100%)] items-center gap-3 rounded-lg border bg-card py-2.5 ps-4 pe-2 text-[14px] text-foreground shadow-md", children: [_jsx("span", { "aria-hidden": true, className: cx('size-2 shrink-0 rounded-full', TONE_FILL[shown.tone]) }), _jsx("span", { dir: "auto", className: "min-w-0 flex-1", children: shown.text }), _jsx(IconButton, { size: "sm", label: dismissLabel, onClick: () => dismissToast(shown.id), className: "size-7", children: "\u00D7" })] }, shown.id)) : null }) }));
}
export const toasts = { toast, useToast, dismissToast };
//# sourceMappingURL=toast.js.map